import React, { createContext, useContext, useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import FormQualityCampaign from '../../../campaign/components/campaignDashboard/quality/FormQualityCampaign'
import ProgressCircleChart from '../../../components/progress/ProgressCircleChart'
import { getPercentage } from '../../../utils/NumberUtil'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import useBoolean from 'utils/customHook/useBoolean'
import { useOutletContext, useParams } from 'react-router'
import useTitle from 'utils/customHook/useTitle'
import Tabs from 'components/Tabs'
import useListIndexed from 'utils/customHook/useListIndexed'
import MapCampaignStation from 'campaign/components/campaignDashboard/MapCampaignStation'
import { isNil, orderBy, take } from 'lodash'
import useActions from 'utils/customHook/useActions'
import moment from 'moment'
import { getDate } from 'utils/DateUtil'
import CampaignAction from 'campaign/actions/CampaignAction'
import { push } from '@lagunovsky/redux-react-router'
import ToastrAction from 'toastr/actions/ToastrAction'
import TableQualityCampaignStations from 'campaign/components/campaignDashboard/quality/tab/TableQualityCampaignStations'
import TableQualityCampaignParameters from 'campaign/components/campaignDashboard/quality/tab/TableQualityCampaignParameters'
import TableQualityCampaignPlannings from 'campaign/components/campaignDashboard/quality/tab/TableQualityCampaignPlannings'
import PopinQualityCampaignStations from 'campaign/components/campaignDashboard/quality/tab/PopinQualityCampaignStations'
import PopinQualityCampaignParameters from 'campaign/components/campaignDashboard/quality/tab/PopinQualityCampaignParameters'
import PopinQualityCampaignPlanning from 'campaign/components/campaignDashboard/quality/tab/PopinQualityCampaignPlanning'
import { negIdGenerator } from 'campaign/utils/CampaignUtils'
import QualityCampaignExportPopup from 'campaign/components/campaignDashboard/quality/QualityCampaignExportPopup'
import { LastUpdateCard } from 'components/card/LastUpdateCard'
import PropTypes from 'prop-types'
import ImportXmlQualityPopup from 'import/components/popup/ImportXmlQualityPopup'
import useCampaignBookmarks from '../../../utils/customHook/useCampaignBookmarks'
import UserAction from '../../../administration/components/user/actions/UserAction'
import { STATION_TYPE_NAME } from '../../../station/constants/StationConstants'

export const QualityCampaignContext = createContext({})

const TABS = {
    STATIONS: 'STATIONS',
    PARAMETERS: 'PARAMETERS',
    PLANNINGS: 'PLANNINGS',
}
const DEFAULT_PLANNING = { stationsPlan: [], parametersPlan: [], selections: [] }

const TabPanel = ({ exportByPlannifActions }) => {
    const {
        isEditMode,
        campaign: { selections: campaignSelections },
        setCampaign,
        campaignStations,
        setCampaignStations,
        campaignParameters,
        setCampaignParameters,
        campaignPlannings,
        setCampaignPlannings,
        setCampaignPlanningsExceptions,
    } = useContext(QualityCampaignContext)

    const {
        qualitometers,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const indexedQualitometers = useListIndexed(qualitometers, 'id')

    const { value: isStationsInList, setTrue: onDisplayList, setFalse: onDisplayMap } = useBoolean(true)
    const { value: isStationPopupOpen, setTrue: onOpenStationPopup, setFalse: onCloseStationPopup } = useBoolean(false)
    const { value: isParameterPopupOpen, setTrue: onOpenParameterPopup, setFalse: onCloseParameterPopup } = useBoolean(false)
    const { value: isNewPlanningOpen, setTrue: onOpenNewPlanning, setFalse: onCloseNewPlanning } = useBoolean(false)

    return (
        <>
            <Tabs
                defaultTab={TABS.STATIONS}
                tabs={[
                    {
                        constant: TABS.STATIONS,
                        label: `${i18n.stations} (${campaignStations.length})`,
                        actions: [
                            {
                                icon: 'note_add',
                                onClick: onOpenStationPopup,
                                displayed: isEditMode,
                            },
                            {
                                icon: 'map',
                                onClick: onDisplayMap,
                                displayed: isStationsInList,
                            },
                            {
                                icon: 'list',
                                onClick: onDisplayList,
                                displayed: !isStationsInList,
                            },
                        ],
                    },
                    {
                        constant: TABS.PARAMETERS,
                        label: `${i18n.parameters} (${campaignParameters.length}) / ${i18n.selections} (${campaignSelections?.length ?? 0})`,
                        actions: [
                            {
                                icon: 'note_add',
                                onClick: onOpenParameterPopup,
                                displayed: isEditMode,
                            },
                        ],
                    },
                    {
                        constant: TABS.PLANNINGS,
                        label: `${i18n.plannings} (${campaignPlannings.length})`,
                        actions: [
                            {
                                icon: 'note_add',
                                onClick: onOpenNewPlanning,
                                displayed: isEditMode,
                            },
                        ],
                    },
                ]}
            >
                {activeTab => (
                    <>
                        {activeTab === TABS.STATIONS && isStationsInList && <TableQualityCampaignStations />}
                        {activeTab === TABS.STATIONS && !isStationsInList && (
                            <MapCampaignStation
                                selectedStations={campaignStations}
                                stations={campaignStations.map(cs => indexedQualitometers[cs.stationId]).filter(q => !isNil(q))}
                                stationType={'quality'}
                            />
                        )}
                        {activeTab === TABS.PARAMETERS && <TableQualityCampaignParameters />}
                        {activeTab === TABS.PLANNINGS && <TableQualityCampaignPlannings exportByPlannifActions={exportByPlannifActions} />}
                    </>
                )}
            </Tabs>
            <PopinQualityCampaignStations
                isOpen={isStationPopupOpen}
                onClose={onCloseStationPopup}
                onValidate={newCampaignStations => {
                    setCampaignStations(newCampaignStations)
                    onCloseStationPopup()
                }}
            />
            <PopinQualityCampaignParameters
                isOpen={isParameterPopupOpen}
                onClose={onCloseParameterPopup}
                onValidate={(newCampaignParameters, newCampaignSelections) => {
                    if (newCampaignParameters) setCampaignParameters(newCampaignParameters)
                    if (newCampaignSelections) setCampaign(p => ({ ...p, selections: newCampaignSelections }))
                    onCloseParameterPopup()
                }}
            />
            <PopinQualityCampaignPlanning
                isOpen={isNewPlanningOpen}
                onClose={onCloseNewPlanning}
                onValidate={(newCampaignPlanning, newCampaignPlanningExceptions) => {
                    setCampaignPlannings(prev => [...prev, newCampaignPlanning])
                    setCampaignPlanningsExceptions(prev => [...prev.filter(({ planningId }) => planningId !== newCampaignPlanning.id), ...newCampaignPlanningExceptions])
                    onCloseNewPlanning()
                }}
                campaignPlanning={{ ...DEFAULT_PLANNING, id: negIdGenerator.next().value }}
                campaignPlanningExceptions={[]}
            />
        </>
    )
}

TabPanel.propTypes = {
    exportByPlannifActions: PropTypes.func,
}

const QualityCampaign = () => {
    const dispatch = useDispatch()
    const params = useParams()
    const {
        campaign: campaignProps,
        campaignStations: campaignStationsProps,
        campaignParameters: campaignParametersProps,
        campaignPlannings: campaignPlanningsProps,
        campaignPlanningsExceptions: campaignPlanningsExceptionsProps,
        campaignProgress,
        edilaboFiles,
    } = useOutletContext()

    const {
        qualitometers,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const { accountUser } = useSelector(store => ({ accountUser: store.AccountReducer.accountUser }), shallowEqual)

    const { value: isExportOpen, setTrue: onOpenExport, setFalse: onCloseExport } = useBoolean(false)

    const { value: isEditMode, setTrue: onEdit, setFalse: onRead } = useBoolean(params.id === 'new')
    const {
        value: isOpenImport,
        setTrue: openImport,
        setFalse: closeImport,
    } = useBoolean(false)
    const [campaign, setCampaign] = useState(campaignProps)
    const [campaignStations, setCampaignStations] = useState(campaignStationsProps)
    const [campaignParameters, setCampaignParameters] = useState(campaignParametersProps)
    const [campaignPlannings, setCampaignPlannings] = useState(campaignPlanningsProps)
    const [campaignPlanningsExceptions, setCampaignPlanningsExceptions] = useState(campaignPlanningsExceptionsProps)
    const [selectedPlanningId, setSelectedCampaignPlanningId] = useState()
    const [isDuplicating, setIsDuplicating] = useState(false)

    useEffect(() => {
        setCampaign(campaignProps)
    }, [campaignProps])
    useEffect(() => {
        setCampaignStations(campaignStationsProps)
    }, [campaignStationsProps])
    useEffect(() => {
        setCampaignParameters(campaignParametersProps)
    }, [campaignParametersProps])
    useEffect(() => {
        setCampaignPlannings(campaignPlanningsProps)
    }, [campaignPlanningsProps])
    useEffect(() => {
        setCampaignPlanningsExceptions(campaignPlanningsExceptionsProps)
    }, [campaignPlanningsExceptionsProps])

    const { getBookmarkAction } = useCampaignBookmarks(STATION_TYPE_NAME.quality)

    useEffect(() => {
        dispatch(UserAction.fetchBookmarks())
    }, [dispatch])

    useTitle(() => [
        {
            title: i18n.quality,
            href: '/quality',
        },
        {
            title: i18n.campaigns,
            href: '/quality/campaign',
        },
        (() => {
            if (isDuplicating) {
                return {
                    title: i18n.duplication,
                    href: `/quality/campaign/${params.id}/dashboard`,
                }
            }
            if (params.id === 'new') {
                return {
                    title: i18n.new,
                    href: '/quality/campaign/new/dashboard',
                }
            }
            return {
                title: campaignProps.name || params.id,
                href: `/quality/campaign/${params.id}/dashboard`,
            }
        })(),
    ], [campaignProps, params.id, isDuplicating])

    useActions(() => {
        if (isEditMode) {
            const onCreate = () => {
                const date = moment().valueOf()
                if (!campaign.name) {
                    dispatch(ToastrAction.error(i18n.thanksToEnterName))
                    return
                }
                const newCampaign = {
                    ...campaign,
                    updateLogin: accountUser.login,
                    updateDate: date,
                    beginningApplication: campaign.beginningApplication || getDate(date),
                    stationType: 3,
                    contactCode: accountUser.contactCode,

                    stations: campaignStations,
                    parameters: campaignParameters,
                    plannings: campaignPlannings,
                    planningsExceptions: campaignPlanningsExceptions,
                }
                dispatch(CampaignAction.insertQualityFullCampaign(newCampaign))
                    .then((id) => {
                        if (isNil(id)) return
                        onRead()
                        setIsDuplicating(false)
                        dispatch(push(`/quality/campaign/${id}/dashboard`))
                    })
            }
            if (isDuplicating) {
                return {
                    save: onCreate,
                    cancel: () => {
                        onRead()
                        setIsDuplicating(false)
                        setCampaign(campaignProps)
                        setCampaignStations(campaignStationsProps)
                        setCampaignParameters(campaignParametersProps)
                        setCampaignPlannings(campaignPlanningsProps)
                        setCampaignPlanningsExceptions(campaignPlanningsExceptionsProps)
                    },
                }
            }
            if (params.id === 'new') {
                return {
                    save: onCreate,
                    // import: () => this.setState({ isPopupImportOpen: true }),
                    cancel: () => dispatch(push('/quality/campaign')),
                }
            }
            return {
                save: () => {
                    const fullCampaign = {
                        ...campaign,
                        stations: campaignStations,
                        parameters: campaignParameters,
                        plannings: campaignPlannings,
                        planningsExceptions: campaignPlanningsExceptions,
                    }
                    dispatch(CampaignAction.updateQualityFullCampaign(fullCampaign)).then(() => {
                        dispatch(CampaignAction.fetchCampaign('quality', params.id))
                        dispatch(CampaignAction.fetchCampaignStation('quality', params.id))
                        dispatch(CampaignAction.fetchCampaignParameters(params.id))
                        dispatch(CampaignAction.fetchCampaignPlannings(params.id))
                        dispatch(CampaignAction.fetchCampaignProgress('quality', params.id))
                        dispatch(CampaignAction.fetchEDILABOFile(params.id)).then(() => onRead())
                    })
                },
                cancel: () => {
                    onRead()
                    setCampaign(campaignProps)
                    setCampaignStations(campaignStationsProps)
                    setCampaignParameters(campaignParametersProps)
                    setCampaignPlannings(campaignPlanningsProps)
                    setCampaignPlanningsExceptions(campaignPlanningsExceptionsProps)
                },
                // import: () => this.setState({ isPopupImportOpen: true }),
            }
        }
        const exportActions = [
            {
                onClick: () => {
                    setSelectedCampaignPlanningId()
                    onOpenExport()
                },
            },
        ]
        if (accountUser.consultant === '1') {
            return {
                exportList: exportActions,
                links: take(orderBy(edilaboFiles, 'updateDate', 'desc'), 5),
            }
        }
        return {
            edit: onEdit,
            delete: () => dispatch(CampaignAction.deleteCampaign('quality', params.id)),
            duplicate: () => {
                setCampaign(p => ({
                    ...p,
                    name: `${i18n.copy} ${p.name}`,
                    beginningApplication: undefined,
                    endingApplication: undefined,
                }))
                let stationIdCorrespondance = {}
                setCampaignStations(prevCS => prevCS.map(s => {
                    const newId = negIdGenerator.next().value
                    stationIdCorrespondance[s.id] = newId
                    return { ...s, id: newId }
                }))

                let parameterIdCorrespondance = {}
                setCampaignParameters(prevCP => prevCP.map(p => {
                    const newId = negIdGenerator.next().value
                    parameterIdCorrespondance[p.id] = newId
                    return { ...p, id: newId }
                }))

                let planningIdCorrespondance = {}
                setCampaignPlannings(prevCP => prevCP.map(p => {
                    const newId = negIdGenerator.next().value
                    planningIdCorrespondance[p.id] = newId
                    return ({
                        ...p,
                        id: newId,
                        stationsPlan: p.stationsPlan.map(id => stationIdCorrespondance[id]),
                        parametersPlan: p.parametersPlan.map(id => parameterIdCorrespondance[id]),
                    })
                }))

                setCampaignPlanningsExceptions(prevCPE => prevCPE.map(pE => ({
                    ...pE,
                    id: undefined,
                    planningId: planningIdCorrespondance[pE.planningId],
                    stationsPlan: pE.stationsPlan.map(id => stationIdCorrespondance[id]),
                    parametersToAddPlan: pE.parametersToAddPlan.map(id => parameterIdCorrespondance[id]),
                    parametersToDeletePlan: pE.parametersToDeletePlan.map(id => parameterIdCorrespondance[id]),
                    selectionsToAddPlan: pE.selectionsToAddPlan ?? [],
                    selectionsToDeletePlan: pE.selectionsToDeletePlan ?? [],
                })))

                setIsDuplicating(true)
                onEdit()
            },
            import: openImport,
            exportList: exportActions,
            links: take(orderBy(edilaboFiles, 'updateDate', 'desc'), 5),
        }
    }, [params.id, isEditMode, isDuplicating, campaign, campaignStations, campaignParameters, campaignPlannings, campaignPlanningsExceptions, edilaboFiles])

    return (
        <QualityCampaignContext.Provider
            value={{
                isEditMode,
                campaign, setCampaign,
                campaignStations, setCampaignStations,
                campaignParameters, setCampaignParameters,
                campaignPlannings, setCampaignPlannings,
                campaignPlanningsExceptions, setCampaignPlanningsExceptions,
            }}
        >
            <Grid container size={12} spacing={1} sx={{ margin: '10px' }}>
                <Grid size={9}>
                    <FormQualityCampaign
                        campaign={campaign}
                        disabled={!isEditMode}
                        onChangeCampaign={obj => setCampaign(p => ({ ...p, ...obj }))}
                        titleActions={params.id !== 'new' && !isDuplicating ? [getBookmarkAction(parseInt(params.id))] : []}
                    />
                </Grid>
                <Grid size={3}>
                    <Stack spacing={1}>
                        <LastUpdateCard
                            element={campaign}
                            withHour
                        />
                        <Card>
                            <CardTitle title={i18n.analysisProgress} />
                            <CardContent>
                                <ProgressCircleChart
                                    total={campaignProgress.progressTotal}
                                    complete={campaignProgress.progress}
                                    title={`${getPercentage(campaignProgress.progress, campaignProgress.progressTotal)} %`}
                                />
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
                {params.id !== 'new' && (
                    <Grid size={12}>
                        <TabPanel
                            exportByPlannifActions={(id) => {
                                setSelectedCampaignPlanningId(id)
                                onOpenExport()
                            }}
                        />
                    </Grid>
                )}
            </Grid>
            {isExportOpen && <QualityCampaignExportPopup
                isOpen={isExportOpen}
                onClose={onCloseExport}
                planningId={selectedPlanningId}
            />}
            {isOpenImport && <ImportXmlQualityPopup
                campaignId={campaign.id}
                callbackImport={() => dispatch(CampaignAction.fetchQualityCampaignOperation(campaign.id))}
                stations={campaignStations.map(({ stationId }) => qualitometers.find(({ id }) => id === stationId)?.code).filter(c => !!c)}
                isOpen={isOpenImport}
                onClose={closeImport}
            />}
            {/* <ImportCampagnePopup
                isOp<GridPopupImportOpen}
                onClose={() => this.setState({ isPopupImportOpen: false })}
                onChangeStation={tmpStations => this.setState({ selectedStations: uniqBy([...selectedStations, ...tmpStations], 'id') })}
                onChangeParameter={tmpParameters => this.setState({ selectedParameters: uniqBy([...selectedParameters, ...tmpParameters], 'parameterCode') })}
            /> */}
        </QualityCampaignContext.Provider>
    )
}

export default QualityCampaign